import { Outlet, Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { canCreateTicket, featureEnabled } from '../lib/features';
import { useRefs } from '../context/RefsContext';
import { UserAvatar } from './UserAvatar';
import { isDtStaff } from '../lib/staff';
import { currentTheme, toggleTheme, THEME_EVENT, type Theme } from '../lib/theme';
import { savePreferences } from '../lib/preferences';
import { isNavItemActive } from '../lib/navActive';

export function AppShell() {
  const { user, logout } = useAuth();
  const { traitants, clients } = useRefs();
  const navigate = useNavigate();
  const loc = useLocation();
  const [showClients, setShowClients] = useState(false);
  const [showTraitants, setShowTraitants] = useState(false);
  // Tiroir du menu : n'a d'effet qu'en dessous du point de rupture (cf. index.css).
  const [menuOpen, setMenuOpen] = useState(false);
  // Theme deja applique sur <html> par theme-init.js : on ne fait que le lire.
  const [theme, setTheme] = useState<Theme>(() => currentTheme());

  // Naviguer referme le tiroir — sinon on reste devant le menu au lieu de voir
  // la page demandee. `loc.search` compte aussi : les filtres ne changent que
  // la chaine de requete.
  useEffect(() => {
    setMenuOpen(false);
  }, [loc.pathname, loc.search]);

  // Le theme peut changer ailleurs (page Profil, preference du compte lue au
  // chargement) : l'icone du bouton suit.
  useEffect(() => {
    const onTheme = () => setTheme(currentTheme());
    window.addEventListener(THEME_EVENT, onTheme);
    return () => window.removeEventListener(THEME_EVENT, onTheme);
  }, []);

  // Bascule clair/sombre, enregistree pour le compte (retrouvee sur tout
  // appareil). Echec d'enregistrement : le choix vaut au moins ici.
  const onToggleTheme = () => {
    const next = toggleTheme();
    setTheme(next);
    void savePreferences({ theme: next }).catch(() => {});
  };

  // Echap referme, comme tout panneau superpose.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="shell">
      {/* Voile : ferme le tiroir d'une tape a cote. Absent sur grand ecran. */}
      <div
        className={`shell-backdrop${menuOpen ? ' is-open' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Menu — tiroir escamotable en dessous de 900 px */}
      <aside id="menu-principal" className={`shell-aside${menuOpen ? ' is-open' : ''}`}>
        <Link to="/me" className="shell-me" aria-label="Mon profil" title="Mon profil">
          <UserAvatar id={user.id_user} size={36} name={user.nom_user || undefined} />
          <div className="shell-me-text">
            <div className="shell-me-name">{user.nom_user || user.mail}</div>
            <div className="shell-me-role">
              {user.is_admin ? 'Admin DT' : isDtStaff(user) ? 'Staff' : user.self_only ? 'Client restreint' : 'Client'}
            </div>
          </div>
        </Link>

        {canCreateTicket(user) && (
          <NavLink to="/tickets/new" className="btn primary" style={{ width: '100%', marginBottom: 12 }}>
            + Nouveau point
          </NavLink>
        )}

        <SidebarSection title="Filtres">
          <NavItem to="/tickets?preset=a_traiter" icon="📥" label="À traiter" />
          <NavItem to="/tickets?preset=mes" icon="👤" label="Mes points" />
          {(user.is_admin || isDtStaff(user)) && (
            <>
              <NavItem to="/tickets?preset=urgents" icon="🚨" label="Urgents (P1-P3)" danger />
              <NavItem to="/tickets?preset=attente_client" icon="⏳" label="En attente client" />
              <NavItem to="/tickets?preset=a_mettre_prod" icon="📤" label="À mettre en prod" />
            </>
          )}
          <NavItem to="/tickets?preset=archives" icon="📁" label="Archives" />
        </SidebarSection>

        {user.is_admin && traitants.length > 0 && (
          <SidebarSection
            title="Par traitant"
            collapsible
            open={showTraitants}
            onToggle={() => setShowTraitants((v) => !v)}
          >
            {traitants.map((t) => (
              <NavItem
                key={t.id_user}
                to={`/tickets?id_support=${t.id_user}`}
                label={`${t.nom_user} (${t.nb_open_tickets})`}
                indented
              />
            ))}
          </SidebarSection>
        )}

        {(user.is_admin || isDtStaff(user)) && clients.length > 0 && (
          <SidebarSection title="Par client" collapsible open={showClients} onToggle={() => setShowClients((v) => !v)}>
            {clients.slice(0, 12).map((c) => (
              <NavItem
                key={c.id_client}
                to={`/tickets?id_client=${c.id_client}`}
                label={`${c.nom_client} (${c.nb_open_tickets})`}
                indented
              />
            ))}
          </SidebarSection>
        )}

        <SidebarSection title="Compte">
          <NavItem to="/me" icon="👤" label="Mon profil" />
        </SidebarSection>

        <SidebarSection title="Outils">
          <NavItem to="/vault/new" icon="🔐" label="Vault de secrets" />
          {featureEnabled(user, 'paybox') && !user.self_only && <NavItem to="/credit/recharge" icon="💳" label="Recharger crédit" />}
          {user.is_admin && <NavItem to="/stats" icon="📈" label="Statistiques clients" />}
          {user.is_admin && <NavItem to="/stats/temps" icon="📅" label="Temps passé interne" />}
          {user.is_admin && <NavItem to="/admin/clients" icon="🏢" label="Comptes clients" />}
          {user.is_admin && <NavItem to="/admin/conges" icon="🌴" label="Substitution congés" />}
        </SidebarSection>
      </aside>

      {/* Main */}
      <div className="shell-body">
        <header className="shell-header">
          <button
            type="button"
            className="btn ghost shell-burger"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={menuOpen}
            aria-controls="menu-principal"
          >
            ☰
          </button>
          <NavLink to="/" className="shell-brand">
            <span className="shell-brand-mark" aria-hidden="true">
              ◆
            </span>
            Jarvis Support
          </NavLink>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn ghost"
            onClick={onToggleTheme}
            aria-label={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
            title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            style={{ fontSize: 'var(--fs-base)', padding: '4px 8px' }}
          >
            <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>
          </button>
          <button
            className="btn ghost shell-logout"
            onClick={handleLogout}
            aria-label="Déconnexion"
            title="Déconnexion"
            style={{ fontSize: 'var(--fs-xs)' }}
          >
            <span className="shell-logout-label">Déconnexion</span>
            <span className="shell-logout-icon" aria-hidden="true">
              ⏻
            </span>
          </button>
        </header>
        <main className="shell-main">
          {user.read_only !== false && (
            <div
              role="status"
              style={{
                fontSize: 'var(--fs-xs)',
                background: 'var(--warning-soft)',
                border: '1px solid var(--warning-border)',
                borderRadius: 6,
                padding: '6px 10px',
                marginBottom: 12,
              }}
            >
              Consultation seule : pendant la transition, les modifications (messages, états, temps…) se font encore dans
              Jarvis V1.
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarSection({
  title,
  children,
  collapsible,
  open,
  onToggle,
}: {
  title: string;
  children: React.ReactNode;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
}) {
  return (
    <div>
      <div
        onClick={collapsible ? onToggle : undefined}
        style={{
          fontSize: 'var(--fs-xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--text-mute)',
          padding: '12px 10px 4px',
          fontWeight: 600,
          cursor: collapsible ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {collapsible && <span style={{ fontSize: 'var(--fs-xs)' }}>{open ? '▾' : '▸'}</span>}
        {title}
      </div>
      {(!collapsible || open) && <div>{children}</div>}
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
  danger,
  indented,
}: {
  to: string;
  icon?: string;
  label: string;
  danger?: boolean;
  indented?: boolean;
}) {
  // Pas de NavLink : il ignore la chaine de requete et compare par prefixe,
  // si bien que sur /tickets/123 tous les liens /tickets?… passaient actifs.
  const { pathname, search } = useLocation();
  const isActive = isNavItemActive(to, pathname, search);
  return (
    <Link
      to={to}
      aria-current={isActive ? 'page' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: indented ? '5px 10px 5px 18px' : '6px 10px',
        borderRadius: 6,
        color: isActive ? 'var(--accent-strong)' : 'var(--text)',
        backgroundColor: isActive ? 'var(--accent-soft)' : 'transparent',
        fontSize: 'var(--fs-sm)',
        fontWeight: isActive ? 500 : 400,
        textDecoration: 'none',
      }}
    >
      {icon && <span style={{ width: '1.2em', flexShrink: 0, textAlign: 'center', opacity: 0.85 }}>{icon}</span>}
      <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{label}</span>
      {danger && <span style={{ width: 4, height: 4, borderRadius: 2, background: 'var(--danger)' }} />}
    </Link>
  );
}
